/*
 * Pretty Panther – behaviour for the pp- sections.
 * Plain script, no dependencies. Everything is progressive enhancement: pages work without it.
 */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const store = {
    get(key, fallback) { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch (e) { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage unavailable */ } },
  };
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  // Horizon scrolls inside .page-wrapper (html/body don't scroll), so window.scrollTo does nothing there.
  const scrollRoot = () => {
    const pw = qs('.page-wrapper');
    return pw && pw.scrollHeight > pw.clientHeight ? pw : (document.scrollingElement || document.documentElement);
  };

  /* ---------------------------------------------------------------- Sliders */
  function initSlider(slider) {
    if (slider.dataset.ppReady) return;
    slider.dataset.ppReady = '1';
    const slides = qsa('.pp-slider__slide', slider);
    if (slides.length < 2) { slider.classList.add('is-ready'); slides[0]?.classList.add('is-active'); return; }
    const dots = qsa('[data-pp-dot]', slider);
    const pauseBtn = qs('[data-pp-pause]', slider);
    const interval = Number(slider.dataset.interval) || 6000;
    let index = 0;
    let timer = null;
    let userPaused = false;

    const show = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => {
        const on = n === index;
        s.classList.toggle('is-active', on);
        s.toggleAttribute('inert', !on);
        if (on) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
        qsa('video', s).forEach((v) => (on ? v.play?.().catch(() => {}) : v.pause?.()));
      });
      dots.forEach((d, n) => (n === index ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
    };
    const stop = () => { clearInterval(timer); timer = null; };
    const start = () => {
      stop();
      if (userPaused || reduceMotion.matches || slider.dataset.autoplay !== 'true') return;
      timer = setInterval(() => show(index + 1), interval);
    };
    const setPaused = (paused) => {
      userPaused = paused;
      slider.classList.toggle('is-paused', paused);
      if (pauseBtn) {
        pauseBtn.setAttribute('aria-pressed', String(paused));
        pauseBtn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      }
      paused ? stop() : start();
    };

    qs('[data-pp-prev]', slider)?.addEventListener('click', () => { show(index - 1); start(); });
    qs('[data-pp-next]', slider)?.addEventListener('click', () => { show(index + 1); start(); });
    dots.forEach((d) => d.addEventListener('click', () => { show(Number(d.dataset.ppDot)); start(); }));
    pauseBtn?.addEventListener('click', () => setPaused(!userPaused));
    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', start);
    slider.addEventListener('focusin', stop);
    slider.addEventListener('focusout', (e) => { if (!slider.contains(e.relatedTarget)) start(); });

    let touchX = null;
    slider.addEventListener('touchstart', (e) => { touchX = e.touches[0].clientX; }, { passive: true });
    slider.addEventListener('touchend', (e) => {
      if (touchX === null) return;
      const dx = e.changedTouches[0].clientX - touchX;
      if (Math.abs(dx) > 40) { show(index + (dx < 0 ? 1 : -1)); start(); }
      touchX = null;
    });

    if (reduceMotion.matches || slider.dataset.autoplay !== 'true') slider.classList.add('is-paused');
    show(0);
    slider.classList.add('is-ready');
    start();
  }

  /* -------------------------------------------------------------- Scrollers */
  function initScroller(el) {
    if (el.dataset.ppReady) return;
    el.dataset.ppReady = '1';
    const list = el.querySelector('ul');
    if (!list) return;
    const update = () => el.classList.toggle('is-scrollable', list.scrollWidth > list.clientWidth + 4);
    qsa('[data-pp-scroll]', el).forEach((btn) =>
      btn.addEventListener('click', () => {
        list.scrollBy({ left: Number(btn.dataset.ppScroll) * list.clientWidth * 0.9, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      })
    );
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(list);
    update();
  }

  /* ---------------------------------------------------------------- Dialogs */
  let lastTrigger = null;
  function openDialog(dialog, trigger) {
    if (!dialog || dialog.open) return;
    lastTrigger = trigger || document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('pp-locked');
    trigger?.setAttribute('aria-expanded', 'true');
  }
  function closeDialog(dialog) {
    if (!dialog?.open) return;
    dialog.close();
  }
  document.addEventListener('close', (e) => {
    if (!(e.target instanceof HTMLDialogElement)) return;
    if (!qs('dialog[open]')) document.documentElement.classList.remove('pp-locked');
    lastTrigger?.setAttribute?.('aria-expanded', 'false');
    lastTrigger?.focus?.();
  }, true);
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-pp-open]');
    if (opener) { e.preventDefault(); openDialog(document.getElementById(opener.dataset.ppOpen), opener); return; }
    const closer = e.target.closest('[data-pp-close]');
    if (closer) { closeDialog(closer.closest('dialog')); return; }
    if (e.target instanceof HTMLDialogElement && e.target.matches('.pp-drawer, .pp-lightbox')) closeDialog(e.target);
  });

  /* ------------------------------------------------------- Listing / filters */
  function gridFetchUrl(grid, url) {
    const u = new URL(url, location.origin);
    u.searchParams.delete('section_id');
    u.searchParams.set('section_id', grid.dataset.sectionId || grid.dataset.sectionFile || 'pp-collection-grid');
    return u.toString();
  }

  async function loadGrid(grid, url, { push = true } = {}) {
    grid.classList.add('is-loading');
    try {
      const res = await fetch(gridFetchUrl(grid, url));
      if (!res.ok) throw new Error(res.status);
      const doc = parse(await res.text());
      const fresh = qs('[data-pp-grid]', doc);
      if (!fresh) throw new Error('no grid');
      const embedded = grid.dataset.ppEmbedded;
      if (embedded) {
        fresh.dataset.ppEmbedded = embedded;
        fresh.dataset.sectionFile = 'pp-collection-grid';
        delete fresh.dataset.sectionId;
        qs('.pp-grid__header', fresh)?.remove();
      }
      qs('dialog[open]', grid)?.close();
      grid.replaceWith(fresh);
      // The filter drawer was removed with the old grid, so its close event never reached the document.
      if (!qs('dialog[open]')) document.documentElement.classList.remove('pp-locked');
      initAll(fresh);
      if (push) {
        const u = new URL(url, location.origin);
        u.searchParams.delete('section_id');
        const target = embedded ? `${location.pathname}${u.search}` : `${u.pathname}${u.search}`;
        history.pushState({ ppGrid: true }, '', target);
        // Bring the top of the new results into view (e.g. after "Next page" at the bottom of the grid).
        const offset = fresh.getBoundingClientRect().top - 120;
        if (offset < 0) scrollRoot().scrollBy({ top: offset, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    } catch (err) {
      // The brand page keeps its server-rendered list if the filterable grid can't load.
      if (push || !grid.dataset.ppEmbedded) location.href = url;
    } finally {
      grid.classList.remove('is-loading');
    }
  }

  function gridBase(grid) {
    return grid.dataset.ppEmbedded || grid.dataset.baseUrl || location.pathname;
  }

  function initGrid(grid) {
    if (grid.dataset.ppReady) return;
    grid.dataset.ppReady = '1';

    // Brand page: replace the server list with the filterable grid from the brand's collection.
    if (grid.dataset.ppGridRemote) {
      grid.dataset.ppEmbedded = grid.dataset.ppGridRemote;
      delete grid.dataset.ppReady;
      loadGrid(grid, `${grid.dataset.ppGridRemote}${location.search}`, { push: false });
      return;
    }

    grid.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-pp-grid-link]');
      if (!link || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      loadGrid(grid, link.href);
    });

    qs('[data-pp-sort] select', grid)?.addEventListener('change', (e) => {
      const u = new URL(location.href);
      const base = new URL(gridBase(grid), location.origin);
      base.search = u.search;
      base.searchParams.set('sort_by', e.target.value);
      base.searchParams.delete('page');
      loadGrid(grid, base.toString());
    });

    qs('[data-pp-filter-form]', grid)?.addEventListener('submit', (e) => {
      e.preventDefault();
      const form = e.target;
      const params = new URLSearchParams();
      for (const [k, v] of new FormData(form)) if (String(v).trim() !== '') params.append(k, v);
      loadGrid(grid, `${gridBase(grid)}?${params.toString()}`);
    });
  }

  window.addEventListener('popstate', () => {
    const grid = qs('[data-pp-grid]');
    if (!grid) return;
    const base = grid.dataset.ppEmbedded || grid.dataset.baseUrl;
    if (base) loadGrid(grid, `${base}${location.search}`, { push: false });
  });

  /* -------------------------------------------------------------------- Bag */
  const CART_STATIC = 'pp-cart-brand-groups';
  const cartSectionIds = () => {
    const ids = new Set(qsa('[data-pp-cart]').map((el) => el.dataset.sectionId));
    if (qs('#pp-cart-drawer')) ids.add(CART_STATIC);
    return Array.from(ids).filter(Boolean);
  };
  const live = () => qs('[data-pp-live]') || (() => {
    const el = document.createElement('div');
    el.setAttribute('data-pp-live', '');
    el.setAttribute('role', 'status');
    el.className = 'visually-hidden';
    document.body.appendChild(el);
    return el;
  })();

  function renderCartSections(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const current = document.getElementById(`shopify-section-${id}`);
      const fresh = parse(html).getElementById(`shopify-section-${id}`);
      if (current && fresh) current.replaceWith(fresh);
      else if (current) current.innerHTML = html;
    });
    const count = qs('[data-pp-cart]')?.dataset.itemCount;
    if (count !== undefined) updateBubble(Number(count));
    initAll(document);
  }

  function updateBubble(count) {
    qsa('.cart-bubble').forEach((b) => b.classList.toggle('visually-hidden', count === 0));
    qsa('.cart-bubble__text-count').forEach((t) => { t.textContent = count > 99 ? '' : String(count); t.classList.toggle('hidden', count === 0); });
  }

  async function cartRequest(path, body) {
    const sections = cartSectionIds();
    const isForm = body instanceof FormData;
    if (isForm) { body.append('sections', sections.join(',')); body.append('sections_url', location.pathname); }
    const payload = isForm ? body : JSON.stringify({ ...body, sections: sections.join(','), sections_url: location.pathname });
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: payload,
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong');
    return data;
  }

  const openCart = (trigger) => {
    const drawer = qs('#pp-cart-drawer');
    if (drawer) openDialog(drawer, trigger);
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-pp-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-pp-error]');
    // A button outside the form (the sticky "Add to bag" bar) can submit it; focus goes back there when the bag closes.
    const trigger = form.ppTrigger || btn;
    form.ppTrigger = null;
    btn?.classList.add('is-loading');
    btn?.setAttribute('aria-busy', 'true');
    if (error) { error.hidden = true; error.textContent = ''; }
    try {
      const data = await cartRequest('cart/add.js', new FormData(form));
      renderCartSections(data.sections);
      live().textContent = `${data.product_title || 'Item'} added to your bag`;
      if (!form.hasAttribute('data-pp-keep-open')) openCart(trigger);
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; } else { live().textContent = err.message; }
    } finally {
      btn?.classList.remove('is-loading');
      btn?.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('click', async (e) => {
    const qtyBtn = e.target.closest('[data-pp-line-qty]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-key]');
      const cart = qtyBtn.closest('[data-pp-cart]');
      if (!line) return;
      cart?.classList.add('is-loading');
      try {
        const data = await cartRequest('cart/change.js', { id: line.dataset.key, quantity: Number(qtyBtn.dataset.ppLineQty) });
        renderCartSections(data.sections);
        live().textContent = 'Bag updated';
        qs('#pp-cart-drawer[open] [data-pp-cart]')?.focus?.();
      } catch (err) {
        live().textContent = err.message;
      } finally {
        cart?.classList.remove('is-loading');
      }
      return;
    }
  });

  // Horizon's header bag button targets its own drawer (#cart-drawer). Catch the click first and
  // open the brand-grouped bag instead, so header-actions.liquid stays untouched for Horizon updates.
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-pp-cart-toggle], [aria-controls="cart-drawer"], [aria-controls="pp-cart-drawer"]');
    if (!toggle || !qs('#pp-cart-drawer')) return;
    e.preventDefault();
    e.stopPropagation();
    toggle.setAttribute('aria-controls', 'pp-cart-drawer');
    openCart(toggle);
  }, true);

  /* ----------------------------------------------------------- Product page */
  function initProduct(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';

    qsa('[data-pp-qty]', root).forEach((btn) =>
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('input');
        const max = Number(input.max) || 99;
        input.value = Math.min(max, Math.max(1, (Number(input.value) || 1) + Number(btn.dataset.ppQty)));
      })
    );

    // Each size is its own URL; swap the section in place and keep the address in step.
    root.addEventListener('click', async (e) => {
      const link = e.target.closest('a[data-pp-variant-link]');
      if (!link || e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      const u = new URL(link.href, location.origin);
      u.searchParams.set('section_id', root.dataset.sectionId);
      try {
        const res = await fetch(u);
        if (!res.ok) throw new Error(res.status);
        const fresh = qs('[data-pp-product]', parse(await res.text()));
        if (!fresh) throw new Error();
        u.searchParams.delete('section_id');
        history.replaceState({}, '', `${u.pathname}${u.search}`);
        root.replaceWith(fresh);
        initAll(fresh);
        qs('[data-pp-variant-link][aria-current="true"]', fresh)?.focus({ preventScroll: true });
      } catch (err) {
        location.href = link.href;
      }
    });

    // Gallery
    const track = qs('[data-pp-gallery-track]', root);
    if (track) {
      const slides = qsa('.pp-gallery__slide', track);
      const thumbs = qsa('[data-pp-thumb]', root);
      const dots = qsa('.pp-gallery__dot', root);
      const go = (i) => track.scrollTo({ left: i * track.clientWidth, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.ppThumb))));
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          thumbs.forEach((t, n) => (n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
          dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
          slides.forEach((s, n) => n !== i && qsa('video', s).forEach((v) => v.pause()));
        });
      }, { passive: true });
    }
    const lightbox = qs('[data-pp-lightbox]', root);
    qsa('[data-pp-zoom]', root).forEach((btn) =>
      btn.addEventListener('click', () => {
        if (!lightbox) return;
        const img = qs('[data-pp-lightbox-img]', lightbox);
        img.src = btn.dataset.ppZoom;
        img.alt = btn.querySelector('img')?.alt || '';
        openDialog(lightbox, btn);
      })
    );
  }

  // Dated delivery estimate in working days (Mon–Fri).
  function addWorkingDays(from, days) {
    const d = new Date(from);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  }
  function initDelivery(el) {
    if (el.dataset.ppReady) return;
    el.dataset.ppReady = '1';
    const min = Number(el.dataset.min);
    const max = Number(el.dataset.max) || min;
    if (!min) return;
    const now = new Date();
    const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { weekday: 'short', day: 'numeric', month: 'short' });
    const a = fmt.format(addWorkingDays(now, min));
    const b = fmt.format(addWorkingDays(now, max));
    el.textContent = el.hasAttribute('data-short') ? `Arrives ${a}${a !== b ? ` – ${b}` : ''}` : `Delivered ${a}${a !== b ? ` – ${b}` : ''}`;
  }

  /* ------------------------------------------------------------ You may like */
  async function initYmal(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const list = qs('[data-pp-ymal-list]', root);
    const fallback = qsa('.pp-tile', list);
    let recs = [];
    try {
      const res = await fetch(root.dataset.url);
      if (res.ok) recs = qsa('[data-pp-recs] .pp-tile', parse(await res.text()));
    } catch (e) { /* keep the server list */ }
    if (!recs.length) return;

    const mode = root.dataset.mode;
    const myVendor = root.dataset.vendor;
    const myId = root.dataset.productId;
    const myPrice = Number(root.dataset.price) || 0;
    const myScents = (root.dataset.scents || '').split(',').filter(Boolean);
    const limit = Number(root.dataset.limit) || 8;
    const perBrand = Number(root.dataset.perBrand) || 2;

    const seen = new Set();
    const pool = [...recs, ...fallback].filter((t) => {
      const id = t.dataset.productId;
      if (seen.has(id) || id === myId || t.dataset.available !== 'true') return false;
      if (mode === 'exclusive' ? t.dataset.vendor !== myVendor : t.dataset.vendor === myVendor) return false;
      seen.add(id);
      return true;
    });
    const score = (t) => {
      let s = 0;
      const scents = (t.dataset.scents || '').split(',');
      if (myScents.some((x) => scents.includes(x))) s += 2;
      const p = Number(t.dataset.price);
      if (myPrice && p >= myPrice * 0.5 && p <= myPrice * 1.5) s += 1;
      return s;
    };
    const ranked = pool.map((t, i) => ({ t, i, s: score(t) })).sort((a, b) => b.s - a.s || a.i - b.i);
    const counts = {};
    const picked = [];
    for (const { t } of ranked) {
      if (picked.length >= limit) break;
      const v = t.dataset.vendor;
      if (mode !== 'exclusive' && (counts[v] || 0) >= perBrand) continue;
      counts[v] = (counts[v] || 0) + 1;
      picked.push(t);
    }
    if (picked.length < Math.min(limit, fallback.length)) return;
    list.replaceChildren(...picked.map((t) => {
      const li = document.createElement('li');
      li.className = 'pp-carousel__item';
      li.appendChild(document.importNode(t, true));
      return li;
    }));
    const scroller = root.querySelector('[data-pp-scroller]');
    if (scroller) { delete scroller.dataset.ppReady; initScroller(scroller); }
  }

  /* --------------------------------------------------------- Recently viewed */
  const RECENT_KEY = 'pp:recent';
  function recordView() {
    const product = qs('[data-pp-recent]');
    const handle = product?.dataset.current;
    if (!handle) return;
    const list = store.get(RECENT_KEY, []).filter((h) => h !== handle);
    list.unshift(handle);
    store.set(RECENT_KEY, list.slice(0, 12));
  }
  async function initRecent(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const current = root.dataset.current;
    const limit = Number(root.dataset.limit) || 6;
    const handles = store.get(RECENT_KEY, []).filter((h) => h !== current).slice(0, limit);
    recordView();
    if (!handles.length) return;
    const tiles = await Promise.all(handles.map(async (h) => {
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(h)}?view=pp-tile`);
        return res.ok ? qs('.pp-tile', parse(await res.text())) : null;
      } catch (e) { return null; }
    }));
    const found = tiles.filter(Boolean);
    if (!found.length) return;
    const list = qs('[data-pp-recent-list]', root);
    list.replaceChildren(...found.map((t) => { const li = document.createElement('li'); li.className = 'pp-carousel__item'; li.appendChild(document.importNode(t, true)); return li; }));
    root.hidden = false;
    const scroller = qs('[data-pp-scroller]', root);
    if (scroller) initScroller(scroller);
  }

  /* --------------------------------------------------------------- Brands A–Z */
  function initAz(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const buttons = qsa('[data-pp-az-filter]', root);
    buttons.forEach((btn) => btn.addEventListener('click', () => {
      const cat = btn.dataset.ppAzFilter;
      buttons.forEach((b) => { const on = b === btn; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', String(on)); });
      qsa('.pp-az__group', root).forEach((group) => {
        let visible = 0;
        qsa('li[data-cats]', group).forEach((li) => {
          const show = !cat || li.dataset.cats.split(' ').includes(cat);
          li.hidden = !show;
          if (show) visible += 1;
        });
        group.classList.toggle('is-empty', visible === 0);
        const letter = qs(`.pp-az__jump a[href="#${group.id}"]`, root);
        if (letter) letter.toggleAttribute('aria-disabled', visible === 0);
      });
    }));
  }

  /* --------------------------------------------------------------- Wishlist */
  // Local fallback until the wishlist app (with account sync) is installed.
  const WISH_KEY = 'pp:wishlist';
  function initWish(btn) {
    if (btn.dataset.ppReady) return;
    btn.dataset.ppReady = '1';
    const id = btn.dataset.ppWishlist;
    const sync = () => btn.setAttribute('aria-pressed', String(store.get(WISH_KEY, []).includes(id)));
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const list = store.get(WISH_KEY, []);
      const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      store.set(WISH_KEY, next);
      qsa(`[data-pp-wishlist="${id}"]`).forEach((b) => b.setAttribute('aria-pressed', String(next.includes(id))));
      live().textContent = next.includes(id) ? 'Saved to wishlist' : 'Removed from wishlist';
    });
    sync();
  }

  /* -------------------------------------------------------------------- Init */
  function initAll(root = document) {
    qsa('[data-pp-slider]', root).forEach(initSlider);
    qsa('[data-pp-scroller]', root).forEach(initScroller);
    qsa('[data-pp-grid]', root).forEach(initGrid);
    if (root.matches?.('[data-pp-grid]')) initGrid(root);
    qsa('[data-pp-product]', root).forEach(initProduct);
    if (root.matches?.('[data-pp-product]')) initProduct(root);
    qsa('[data-pp-delivery]', root).forEach(initDelivery);
    qsa('[data-pp-ymal]', root).forEach(initYmal);
    qsa('[data-pp-recent]', root).forEach(initRecent);
    qsa('[data-pp-az]', root).forEach(initAz);
    qsa('[data-pp-wishlist]', root).forEach(initWish);
    if (root.matches?.('[data-pp-scroller]')) initScroller(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  // Re-initialise sections the theme editor reloads.
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
